import { NgClass } from "@angular/common";
import { HttpClient } from "@angular/common/http";
import { Component, Input } from "@angular/core";
import { PonderDataClient } from "@apiserver-rest/ponder-data-client";
import { GameType } from "@apiserver-rest/ponder-data-client-base";
import { PonderTasksClient } from "@apiserver-rest/ponder-tasks-client";
import { ExportCollectionRequest, ExportCollectionResponse, ExternalCollectionType } from "@apiserver-rest/ponder-tasks-client-base";
import { AuthService } from "@ponder-core/auth/auth.service";
import { EventBroker } from "@ponder-core/events/eventBroker";
import { BusySpinComponent } from "@ponder-shared/components/busy-spin.component";
import { ExportCollectionResultsComponent } from "./export-collection-results.component";
import { take } from "rxjs";
import { RateLimitedError } from "@ponder-shared/errors/rate-limited-error";
import { AuthSession } from "@ponder-core/auth/auth-session";

@Component({
    imports: [NgClass, BusySpinComponent, ExportCollectionResultsComponent],
    templateUrl: "./export-collection.component.html"
})
export class ExportCollectionComponent {
    @Input({ required: true }) public username!: string;

    public isOwnCollection: boolean = false;
    
    public gameTypes = GameType;
    public collectionFormats = ExternalCollectionType;

    public isExporting: boolean = false;
    public exportFailure: boolean = false;
    public failureReason?: string;
    public isThrottled: boolean = false;
    public oneAndDone: boolean = false;

    private currGame: GameType = GameType.Magic;
    private currFormat: ExternalCollectionType = ExternalCollectionType.Csv;
    
    constructor(
        private ponderDataClient: PonderDataClient, 
        private ponderTasksClient: PonderTasksClient, 
        private authService: AuthService, 
        private httpClient: HttpClient,
        private events: EventBroker) {
        
    }

    public ngOnInit(): void {
        this.authService.getSessionAsync().then((authSession?: AuthSession) => {
                if (authSession?.username && (authSession.username == this.username)) {
                    this.isOwnCollection = true;
                }
            });
    }

    // -------------------------------------------------------------------------------------
    // Game Select
    // -------------------------------------------------------------------------------------

    public onGameSelected(gameType: GameType): void {
        if (this.currGame && (this.currGame == gameType)) {
            return;
        }

        this.currGame = gameType;
        this.onFormatSelected(ExternalCollectionType.Csv);
    }

    public isGameSelected(gameType: GameType): boolean {
        return (this.currGame == gameType);
    }
    
    // -------------------------------------------------------------------------------------
    // Format Select
    // -------------------------------------------------------------------------------------

    public onFormatSelected(collectionFormat: ExternalCollectionType): void {
        if (this.currFormat && (this.currFormat == collectionFormat)) {
            return;
        }

        this.currFormat = collectionFormat;
    }

    public isFormatSelected(collectionFormat: ExternalCollectionType): boolean {
        return (this.currFormat == collectionFormat);
    }
    
    // -------------------------------------------------------------------------------------
    // Export
    // -------------------------------------------------------------------------------------

    public onExportClicked(): void {
        if (this.isExporting || this.isThrottled || this.oneAndDone) {
            return;
        }

        this.isExporting = true;
        this.exportFailure = false;

        this.ponderTasksClient
            .userCollectionExport(new ExportCollectionRequest({
                game: this.currGame,
                collectionType: this.currFormat
            }))
            .pipe(take(1))
            .subscribe({
                next: (response: ExportCollectionResponse) => {
                    if (response.success && response.status) {
                        this.events.collection.collectionExportedEvent.emit(response.status);
                        this.onExportComplete(true);
                    } else {
                        this.onExportComplete(false, "Failed to initiate collection export.");
                    }
                },
                error: (e: any) => {
                    if (e.status == 429) {
                        var rateLimitedError = JSON.parse(e.response) as RateLimitedError;
                        this.isThrottled = true;
                        this.onExportComplete(false, `Slow down! You have reached your hourly limit (${rateLimitedError.limit}) on collection exports.`);
                    } else {
                        this.onExportComplete(false, "Failed to initiate collection export.");
                    }
                }
            })
            .add(() => this.isExporting = false);
    }

    private onExportComplete(success: boolean, failureReason?: string): void {
        this.isExporting = false;
        this.exportFailure = !success;
        this.failureReason = failureReason;

        if (success) {
            this.oneAndDone = true;
        }
    }
}